import React, { useRef, useEffect } from 'react'
import { Card, Spinner, Button } from 'react-bootstrap'
import moment from 'moment'
import { Chat, Message } from './types'
import MessageCard from './message-card'
import CallCard from './call-card'
import AttachmentMessageCard from './attachment-message-card'
import MessageWithAttachmentCard from './message-with-attachment-card'
import { getMessageBackgroundColor } from 'utils/message-colors'
import { truncateString } from 'utils/string'

interface ConversationsCardProps {
  selectedChat: Chat | null
  messages: Message[]
  messagesLoading: boolean
  getOtherPhoneNumber: (chat: Chat) => string
  formatTimestamp: (timestamp: string) => string
  onBack: () => void
}

const ConversationsCard: React.FC<ConversationsCardProps> = ({
  selectedChat,
  messages,
  messagesLoading,
  getOtherPhoneNumber,
  formatTimestamp,
  onBack,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const isPhoneNumber = (str: string) => {
    // Check if string looks like a phone number (contains mostly digits, +, -, (, ), spaces)
    return /^[\d\s\-+()]+$/.test(str?.trim() || '')
  }

  const getStatusIcon = (message: Message) => {
    const status = message.delivery_status

    if (status === 'delivered') {
      return 'Delivered'
    }
    if (status === 'paused') {
      return 'Paused'
    }
    if (status === 'service_unavailable' || status === 'rate_limit_exceeded') {
      return 'Not Delivered'
    }
    return 'Sent'
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  return (
    <Card
      className="mt-4 d-flex flex-column"
      style={{
        height: 'clamp(400px, 70vh, 700px)',
        minHeight: '400px',
      }}
    >
      <Card.Header
        className="d-flex align-items-center"
        style={{ padding: 'clamp(10px, 3vw, 10px)' }}
      >
        <Button
          variant="link"
          onClick={onBack}
          className="me-3 d-lg-none p-0"
          style={{
            fontSize: 'clamp(20px, 4vw, 24px)',
            color: '#4ea6ff',
          }}
        >
          <ion-icon name="chevron-back-outline" />
        </Button>
        <div>
          <h5
            className={
              selectedChat &&
              selectedChat.display_name?.trim() &&
              !isPhoneNumber(selectedChat.display_name)
                ? 'mb-0'
                : 'mb-3'
            }
            style={{ fontSize: 'clamp(16px, 3.5vw, 20px)' }}
          >
            {selectedChat &&
            selectedChat.display_name?.trim() &&
            !isPhoneNumber(selectedChat.display_name)
              ? truncateString(selectedChat.display_name, 50)
              : selectedChat
              ? getOtherPhoneNumber(selectedChat)
              : ''}
          </h5>
          {selectedChat &&
            selectedChat.display_name?.trim() &&
            !isPhoneNumber(selectedChat.display_name) && (
              <small
                className="text-muted"
                style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}
              >
                {getOtherPhoneNumber(selectedChat)}
              </small>
            )}
        </div>
      </Card.Header>
      <Card.Body
        className="flex-grow-1"
        style={{
          overflowY: 'auto',
          padding: selectedChat ? 'clamp(12px, 3vw, 20px)' : '0',
        }}
      >
        {selectedChat ? (
          <>
            {messagesLoading ? (
              <div className="text-center p-4">
                <Spinner animation="border" />
                <p className="mt-2">Loading messages...</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center p-4">
                <p>No messages in this conversation</p>
              </div>
            ) : (
              messages.map((message, index) => {
                const showTimestamp =
                  index === 0 ||
                  moment(message.sent_at).diff(
                    moment(messages[index - 1].sent_at),
                    'minutes'
                  ) > 5

                // Group message logic
                const isGroupChat = selectedChat?.group || false
                const isFirstInGroup =
                  index === 0 ||
                  messages[index - 1].chat_handle_id !==
                    message.chat_handle_id ||
                  moment(message.sent_at).diff(
                    moment(messages[index - 1].sent_at),
                    'minutes'
                  ) > 5

                // Calculate background color once using message's service
                const backgroundColor = getMessageBackgroundColor(
                  message.is_from_me,
                  message.service
                )

                // Check message type and route to appropriate component
                const isCall =
                  message.eventable_type === 'Call' ||
                  message.message_type === 'call_finished' ||
                  message.message_type === 'call_started' ||
                  message.message_type === 'call_processed'

                const hasAttachments =
                  message.has_attachments && message.attachments?.length > 0
                const hasText = message.text && message.text.trim().length > 0

                if (isCall) {
                  return (
                    <CallCard
                      key={message.id}
                      message={message}
                      showTimestamp={showTimestamp}
                      formatTimestamp={formatTimestamp}
                    />
                  )
                }

                //  Message with both text and attachments
                if (hasAttachments && hasText) {
                  return (
                    <MessageWithAttachmentCard
                      key={message.id}
                      message={message}
                      showTimestamp={showTimestamp}
                      getStatusIcon={getStatusIcon}
                      formatTimestamp={formatTimestamp}
                      backgroundColor={backgroundColor}
                      isGroupChat={isGroupChat}
                      isFirstInGroup={isFirstInGroup}
                    />
                  )
                }

                // Message with only attachments
                if (hasAttachments) {
                  return (
                    <AttachmentMessageCard
                      key={message.id}
                      message={message}
                      showTimestamp={showTimestamp}
                      getStatusIcon={getStatusIcon}
                      formatTimestamp={formatTimestamp}
                      isGroupChat={isGroupChat}
                      isFirstInGroup={isFirstInGroup}
                    />
                  )
                }

                return (
                  <MessageCard
                    key={message.id}
                    message={message}
                    showTimestamp={showTimestamp}
                    getStatusIcon={getStatusIcon}
                    formatTimestamp={formatTimestamp}
                    backgroundColor={backgroundColor}
                    isGroupChat={isGroupChat}
                    isFirstInGroup={isFirstInGroup}
                  />
                )
              })
            )}
            <div ref={messagesEndRef} />
          </>
        ) : (
          <div className="d-flex align-items-center justify-content-center h-100">
            <div className="text-center">
              <h5>Select a conversation</h5>
              <p className="text-muted">
                Choose a conversation from the left to view messages
              </p>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

export default ConversationsCard
